import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  EmptyState,
  Input,
  List,
  Modal,
  enterDelay,
} from "@playbookai/ui";
import {
  SECTION_LABELS,
  type KnowledgeArticle,
  type KnowledgeDomainId,
  type KnowledgeSection,
} from "../content/index.js";
import { knowledgeArticlePath } from "../routes.js";
import {
  searchKnowledgeArticles,
  type KnowledgeSearchFilters,
} from "../search.js";
import { useKnowledgeStudio } from "../KnowledgeStudioProvider.js";
import { audiencePath, matchesAudience } from "../content/audience.js";
import { KnowledgeSearchResultRow } from "./KnowledgeSearchResultRow.js";

export { findKnowledgeArticles } from "../search.js";

export interface KnowledgeSearchPreset extends KnowledgeSearchFilters {
  readonly query?: string;
}

// Kept hand-built (not SegmentedControl): the element map names that part,
// but both filter groups below are a variable-length, wrapping option set —
// SegmentedControl is a fixed, non-wrapping track. ToggleGroup (OK-11) fits
// this aria-pressed pattern, but that row is not one of T01's approved rows.
function FilterButton({
  active,
  children,
  onClick,
}: {
  readonly active: boolean;
  readonly children: string;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal motion-reduce:transition-none ${
        active
          ? "border-pb-teal/40 bg-pb-teal-soft text-pb-teal-text"
          : "border-pb-border bg-pb-card text-pb-text-secondary hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * The Knowledge Studio search dialog.
 *
 * `open` reaches the `Modal` and nothing else. It used to gate an early
 * `return null` in a wrapper above this component, which destroyed the dialog
 * on the frame the flag flipped and made the chassis's own exit unreachable
 * (ADR-0091). The host remounts this component per search request, so a preset
 * still seeds the fields on every opening.
 */
export function KnowledgeSearchDialog({
  instanceId,
  open,
  preset,
  onClose,
}: {
  readonly instanceId: string;
  readonly open: boolean;
  readonly preset?: KnowledgeSearchPreset | undefined;
  readonly onClose: () => void;
}): JSX.Element {
  const { articles, domains, audience } = useKnowledgeStudio();
  const candidates = useMemo(
    () => articles.filter((article) => matchesAudience(article, audience)),
    [articles, audience],
  );
  const [query, setQuery] = useState(preset?.query ?? "");
  const [domainId, setDomainId] = useState<KnowledgeDomainId | undefined>(
    preset?.domainId,
  );
  const [section, setSection] = useState<KnowledgeSection | undefined>(
    preset?.section,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const results = useMemo(
    () => searchKnowledgeArticles(query, { domainId, section }, candidates),
    [domainId, query, section, candidates],
  );
  const hasFilters = Boolean(domainId || section);

  const openArticle = (article: KnowledgeArticle) => {
    navigate(
      audiencePath(
        knowledgeArticlePath(instanceId, article.domainId, article.slug),
        audience,
      ),
    );
    onClose();
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "ArrowDown" || results.length === 0) return;
    event.preventDefault();
    resultsRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
  };

  const clearFilters = () => {
    setDomainId(undefined);
    setSection(undefined);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Search Knowledge Studio"
      description="Find guidance by topic, practice, or the work you need to do."
      size="lg"
      placement="top"
      initialFocusRef={inputRef}
    >
      <Input
        ref={inputRef}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={onInputKeyDown}
        aria-label="Search Knowledge Studio articles"
        placeholder="Try ‘future state’, ‘coaching’, or ‘TAGS’"
        prefix={<Search aria-hidden="true" className="h-4 w-4" />}
      />

      <div className="mt-5 space-y-4 border-b border-pb-border pb-5">
        <fieldset>
          <legend className="text-xs font-semibold text-pb-text-muted">
            Practice
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            <FilterButton
              active={!domainId}
              onClick={() => setDomainId(undefined)}
            >
              All practices
            </FilterButton>
            {domains
              .filter((domain) =>
                candidates.some((article) => article.domainId === domain.id),
              )
              .map((domain) => (
                <FilterButton
                  key={domain.id}
                  active={domainId === domain.id}
                  onClick={() => setDomainId(domain.id)}
                >
                  {domain.name}
                </FilterButton>
              ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="text-xs font-semibold text-pb-text-muted">
            Guide type
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            <FilterButton
              active={!section}
              onClick={() => setSection(undefined)}
            >
              All types
            </FilterButton>
            {(Object.keys(SECTION_LABELS) as KnowledgeSection[]).map((item) => (
              <FilterButton
                key={item}
                active={section === item}
                onClick={() => setSection(item)}
              >
                {SECTION_LABELS[item]}
              </FilterButton>
            ))}
            {hasFilters ? (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X aria-hidden="true" className="h-3.5 w-3.5" />
                Clear filters
              </Button>
            ) : null}
          </div>
        </fieldset>
      </div>

      <div className="mt-5 flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-pb-text">
          {query.trim() || hasFilters
            ? "Matching guidance"
            : "Suggested starting points"}
        </p>
        <p aria-live="polite" className="text-xs text-pb-text-muted">
          {results.length} {results.length === 1 ? "article" : "articles"}
        </p>
      </div>

      {results.length > 0 ? (
        <List
          as="ul"
          ref={resultsRef}
          aria-label="Search results"
          className="mt-3 max-h-[46vh] overflow-y-auto border-y border-pb-border pe-1"
        >
          {results.map((result, index) => (
            <KnowledgeSearchResultRow
              key={result.article.id}
              result={result}
              className="pb-enter-row"
              style={enterDelay(index) as CSSProperties}
              onOpen={openArticle}
              onMove={(direction) => {
                const buttons = Array.from(
                  resultsRef.current?.querySelectorAll<HTMLButtonElement>(
                    "button",
                  ) ?? [],
                );
                const nextIndex =
                  (index + direction + buttons.length) % buttons.length;
                buttons[nextIndex]?.focus();
              }}
            />
          ))}
        </List>
      ) : (
        // OK-31/empty-state-page-tone-framed: the hand-built dashed frame
        // becomes Card variant="dashed" — EmptyState has no frame of its own.
        <Card variant="dashed" className="mt-3">
          <EmptyState
            variant="no-results"
            title="No matching guidance"
            description="Try fewer words, clear a filter, or search for a practice name."
            {...(query
              ? {
                  action: (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setQuery("")}
                    >
                      Clear search
                    </Button>
                  ),
                }
              : {})}
            {...(hasFilters
              ? {
                  secondaryAction: (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={clearFilters}
                    >
                      Search all practices
                    </Button>
                  ),
                }
              : {})}
          />
        </Card>
      )}
    </Modal>
  );
}
